/**
 * VisualRangeBar.tsx
 *
 * Visual range bar matching legacy quest-lab-details.php / vibrant-lab-details.php.
 * Red background for out-of-range, blue for normal range, tick mark for actual value.
 */

interface VisualRangeBarProps {
  value: string;
  referenceRange: string;
  abnormalFlag?: string | null;
}

function parseNumeric(val: string): number | null {
  const cleaned = val.replace(/[^0-9.\-]/g, '');
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : null;
}

function parseRange(refRange: string): { low: number | null; high: number | null } {
  if (!refRange) return { low: null, high: null };

  const trimmed = refRange.trim();

  if (trimmed.startsWith('<') || trimmed.startsWith('<=')) {
    const val = parseNumeric(trimmed.replace(/^<=?/, ''));
    return { low: null, high: val };
  }
  if (trimmed.startsWith('>') || trimmed.startsWith('>=')) {
    const val = parseNumeric(trimmed.replace(/^>=?/, ''));
    return { low: val, high: null };
  }

  const dashMatch = trimmed.match(/^([0-9.]+)\s*[-–]\s*([0-9.]+)$/);
  if (dashMatch) {
    return { low: parseNumeric(dashMatch[1]), high: parseNumeric(dashMatch[2]) };
  }

  return { low: null, high: null };
}

export function VisualRangeBar({ value, referenceRange, abnormalFlag }: VisualRangeBarProps) {
  const numValue = parseNumeric(value);
  const { low, high } = parseRange(referenceRange);

  if (numValue === null || (low === null && high === null)) return null;

  const effectiveLow = low ?? (high !== null ? 0 : 0);
  const effectiveHigh = high ?? (low !== null ? low * 2 : 100);
  const rangeSpan = effectiveHigh - effectiveLow;
  if (rangeSpan <= 0) return null;

  const margin = rangeSpan * 0.3;
  const barMin = effectiveLow - margin;
  const barMax = effectiveHigh + margin;
  const barSpan = barMax - barMin;

  const clampedValue = Math.max(barMin, Math.min(barMax, numValue));
  const valuePct = ((clampedValue - barMin) / barSpan) * 100;
  const lowPct = low !== null ? ((effectiveLow - barMin) / barSpan) * 100 : 0;
  const highPct = high !== null ? ((effectiveHigh - barMin) / barSpan) * 100 : 100;
  const normalWidth = highPct - lowPct;

  const isAbnormal = abnormalFlag && abnormalFlag !== 'N' && abnormalFlag !== '';

  return (
    <div className="mt-1" style={{ width: '100%', maxWidth: 200 }}>
      <div className="relative h-3 rounded-sm overflow-hidden" style={{ backgroundColor: '#eda3a3' }}>
        <div
          className="absolute top-0 h-full"
          style={{
            backgroundColor: '#c4d9f1',
            left: `${lowPct}%`,
            width: `${normalWidth}%`,
          }}
        />
        <div
          className="absolute top-0 h-full"
          style={{
            left: `${valuePct}%`,
            width: 2,
            backgroundColor: isAbnormal ? '#7b0000' : '#000',
            transform: 'translateX(-1px)',
          }}
        />
      </div>
      <div className="flex justify-between text-[9px] text-slate-400 mt-0.5">
        {low !== null && <span>{low}</span>}
        {low === null && <span />}
        {high !== null && <span>{high}</span>}
      </div>
    </div>
  );
}

export const FLAG_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  N:  { bg: '#ffffff', text: '#000000', label: 'Normal' },
  L:  { bg: '#fff4de', text: '#653901', label: 'Below low normal' },
  LL: { bg: '#fbc9c9', text: '#7b0000', label: 'Below lower panic limits' },
  H:  { bg: '#fff4de', text: '#653901', label: 'Above high normal' },
  HH: { bg: '#fbc9c9', text: '#7b0000', label: 'Above upper panic limits' },
  A:  { bg: '#fff4de', text: '#653901', label: 'Alpha abnormal' },
  AA: { bg: '#fbc9c9', text: '#7b0000', label: 'Alpha very abnormal' },
};
